import { CartAddEvent, CartErrorEvent } from '@theme/events';

class StateBundleSelector extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-add-to-cart]');
    this.label = this.button?.querySelector('[data-label]');
    this.errorEl = this.querySelector('[data-error]');

    this.stickyBar = this.querySelector('[data-sticky-bar]');
    this.stickySelection = this.querySelector('[data-sticky-selection]');
    this.stickyButton = this.querySelector('[data-sticky-add-to-cart]');

    if (this.stickyBar) {
      // Move to a direct child of <body> so its fixed positioning and
      // z-index aren't scoped inside any ancestor stacking context.
      document.body.appendChild(this.stickyBar);
    }

    this.addEventListener('change', this.handleChange);
    this.button?.addEventListener('click', this.handleClick);
    this.stickyButton?.addEventListener('click', this.handleClick);

    this.syncButton();
    this.setupStickyObserver();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleChange);
    this.button?.removeEventListener('click', this.handleClick);
    this.stickyButton?.removeEventListener('click', this.handleClick);
    this.observer?.disconnect();
    this.stickyBar?.remove();
  }

  handleChange = (event) => {
    if (!event.target.matches('.state-bundle-option__input')) return;
    this.syncButton();
  };

  syncButton() {
    const selected = this.querySelector('.state-bundle-option__input:checked');
    if (!selected || !this.button) return;

    this.button.dataset.variantId = selected.dataset.variantId;
    if (this.label) {
      this.label.textContent = `Order ${selected.dataset.displayName} — ${selected.dataset.price} →`;
    }

    if (this.stickyButton) {
      this.stickyButton.dataset.variantId = selected.dataset.variantId;
    }
    if (this.stickySelection) {
      this.stickySelection.textContent = `${selected.dataset.displayName} · ${selected.dataset.price}`;
    }
  }

  setupStickyObserver() {
    if (!this.stickyBar || !this.button) return;

    this.observer = new IntersectionObserver(
      ([entry]) => {
        const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.stickyBar.classList.toggle('is-visible', scrolledPast);
      },
      { threshold: 0 }
    );
    this.observer.observe(this.button);
  }

  handleClick = async () => {
    const variantId = Number(this.button?.dataset.variantId);
    if (!variantId || this.button?.disabled) return;

    if (this.button) this.button.disabled = true;
    if (this.stickyButton) this.stickyButton.disabled = true;
    this.errorEl?.setAttribute('hidden', '');

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        this.dispatchEvent(
          new CartErrorEvent(this.id, result.message, result.description, result.errors)
        );
        if (this.errorEl) {
          this.errorEl.textContent = result.message || 'Could not add this item to your cart.';
          this.errorEl.removeAttribute('hidden');
        }
        return;
      }

      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      document.dispatchEvent(
        new CartAddEvent(cart, this.id, {
          source: 'state-bundle-selector',
          itemCount: 1,
        })
      );
    } catch (error) {
      console.error('STATE bundle add-to-cart failed', error);
      if (this.errorEl) {
        this.errorEl.textContent = 'Something went wrong. Please try again.';
        this.errorEl.removeAttribute('hidden');
      }
    } finally {
      if (this.button) this.button.disabled = false;
      if (this.stickyButton) this.stickyButton.disabled = false;
    }
  };
}

if (!customElements.get('state-bundle-selector')) {
  customElements.define('state-bundle-selector', StateBundleSelector);
}
